import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Accordion } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { AreaSection, ControlAction } from '../../controlCenterMeta';
import type { ActionRefusals, AreaKey } from '../../useControlCenterPage';
import { CatalogAccordionItem } from './CatalogAccordionItem';
import { CatalogRow } from './CatalogRow';
import { CatalogSection } from './CatalogSection';

type ActionCatalogProps = {
    sections: AreaSection[];
    availableCount: number;
    areaCount: number;
    isCompact: boolean;
    expandedAreas: Set<AreaKey>;
    onExpandedAreasChange: (keys: Set<AreaKey>) => void;
    isPinned: (id: string) => boolean;
    refusals: ActionRefusals;
    onRun: (action: ControlAction) => void;
    onTogglePin: (id: string) => void;
};

type SectionRowsProps = Pick<
    ActionCatalogProps,
    'isPinned' | 'refusals' | 'onRun' | 'onTogglePin'
> & {
    section: AreaSection;
};

/**
 * Everything the app can do, in one card.
 *
 * The whole design of this list is what it does *not* carry. Where an action
 * lands is one glyph explained once in this header instead of a legend per row,
 * and colour is absent entirely — it lives in the pinned grid above, where six
 * tinted chips still mean something.
 *
 * It used to carry one more thing: an Available / Coming soon segmented filter,
 * with eight roadmap rows behind the second half. BOS-181 removed both. A
 * launcher is a list of what you can press, and a second list of what you
 * cannot belongs wherever the roadmap lives — not one tab away from the
 * buttons.
 *
 * The counts come from the catalog rather than from this file: a header that
 * says "26 across 5 areas" is a number that goes stale the first time an action
 * moves.
 *
 * Two shapes, chosen by viewport rather than by CSS — see `useCompactCatalog`.
 * Below `md` the areas are one `Accordion`; above it they are always-open
 * sections with no toggle at all.
 */
export const ActionCatalog: React.FC<ActionCatalogProps> = ({
    sections,
    availableCount,
    areaCount,
    isCompact,
    expandedAreas,
    onExpandedAreasChange,
    isPinned,
    refusals,
    onRun,
    onTogglePin,
}) => {
    const { t } = useTranslation('pages');

    const groups = sections.map((section) => ({
        area: section.area,
        count: section.actions.length,
        rows: (
            <SectionRows
                isPinned={isPinned}
                onRun={onRun}
                refusals={refusals}
                onTogglePin={onTogglePin}
                section={section}
            />
        ),
    }));

    return (
        <section
            aria-label={t('controlCenter.catalog.title')}
            className="flex flex-col gap-4 rounded-2xl border border-separator bg-surface p-4 sm:p-5"
            {...getNodeTestIdProp('control-center-catalog')}
        >
            <div className="flex min-w-0 flex-col gap-0.5">
                <h2 className="text-base font-medium tracking-tight text-foreground">
                    {t('controlCenter.catalog.title')}
                </h2>
                <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
                    {t('controlCenter.catalog.subtitle', {
                        count: availableCount,
                        areas: areaCount,
                    })}
                    <Icon
                        aria-hidden
                        className="size-3.5"
                        icon="solar:arrow-right-up-linear"
                    />
                    {t('controlCenter.catalog.legend')}
                </p>
            </div>

            {isCompact ? (
                <Accordion
                    allowsMultipleExpanded
                    expandedKeys={expandedAreas}
                    onExpandedChange={onExpandedAreasChange}
                    {...getNodeTestIdProp('control-center-catalog-accordion')}
                >
                    {groups.map((group) => (
                        <CatalogAccordionItem
                            area={group.area}
                            count={group.count}
                            key={group.area}
                        >
                            {group.rows}
                        </CatalogAccordionItem>
                    ))}
                </Accordion>
            ) : (
                <div className="flex flex-col gap-5">
                    {groups.map((group) => (
                        <CatalogSection
                            area={group.area}
                            count={group.count}
                            key={group.area}
                        >
                            {group.rows}
                        </CatalogSection>
                    ))}
                </div>
            )}
        </section>
    );
};

const SectionRows: React.FC<SectionRowsProps> = ({
    section,
    isPinned,
    refusals,
    onRun,
    onTogglePin,
}) => (
    <>
        {section.actions.map((action) => (
            <CatalogRow
                action={action}
                isPinned={isPinned(action.id)}
                key={action.id}
                onRun={onRun}
                onTogglePin={onTogglePin}
                refusals={refusals}
            />
        ))}
    </>
);
